import { Link, useForm } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useBasePath } from '@/lib/paths'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { CalendarDays, Clock, MapPin, Video, User, X } from 'lucide-react'
import type { NotarizationAppointment } from '@/types'

type Props = {
  notarization: { id: string; type_name: string; business_name: string; session_mode: string }
  appointment: NotarizationAppointment
}

export default function AppointmentShow({ notarization, appointment }: Props) {
  const basePath = useBasePath()
  const confirm = useConfirm()
  const cancelForm = useForm({})

  const handleCancel = async () => {
    if (!await confirm({
      description: 'Are you sure you want to cancel this appointment? You can book a new one afterwards.',
      confirmLabel: 'Cancel Appointment',
      destructive: true,
    })) return
    cancelForm.delete(`${basePath}/notarizations/${notarization.id}/appointment`)
  }

  const scheduledDate = new Date(appointment.scheduled_at)

  return (
    <DashboardLayout
      title="Appointment"
      breadcrumbs={[
        { label: 'Notarizations', href: 'notarizations' },
        { label: notarization.business_name, href: `notarizations/${notarization.id}` },
        { label: 'Appointment' }
      ]}
    >
      <div>
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <CalendarDays className="w-4 h-4 text-muted-foreground" />
            <h1 className="text-xl font-semibold tracking-tight">Appointment</h1>
            {appointment.cancelled ? (
              <Badge className="font-normal text-xs bg-destructive text-destructive-foreground">Cancelled</Badge>
            ) : (
              <Badge className="font-normal text-xs bg-blue-600 text-white">Scheduled</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{notarization.type_name}</p>
        </div>

        <div className="space-y-6">
          {/* Appointment Details */}
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Appointment Details</h2>
            </div>
            <div className="px-4 py-3 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <User className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{appointment.notary_name}</p>
                  <p className="text-xs text-muted-foreground">Notary Public</p>
                </div>
              </div>

              <div className="h-px bg-border" />

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-foreground">
                  <CalendarDays className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span>
                    {scheduledDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-foreground">
                  <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span>
                    {scheduledDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    {' '}({appointment.duration_minutes} minutes)
                  </span>
                </div>
                <div className="flex items-center gap-2 text-foreground">
                  {notarization.session_mode === 'ron' ? (
                    <>
                      <Video className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span>Remote Online (RON)</span>
                    </>
                  ) : (
                    <>
                      <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span>In Person</span>
                    </>
                  )}
                </div>
                {appointment.timezone && (
                  <div className="text-xs text-muted-foreground pl-6">
                    Timezone: {appointment.timezone}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" className="flex-1">
              <Link href={`${basePath}/notarizations/${notarization.id}`}>
                Back to Notarization
              </Link>
            </Button>
            {!appointment.cancelled && (
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleCancel}
                disabled={cancelForm.processing}
              >
                <X className="w-4 h-4 mr-1.5" />
                {cancelForm.processing ? 'Cancelling...' : 'Cancel Appointment'}
              </Button>
            )}
          </div>

          {/* Cancelled notice */}
          {appointment.cancelled && (
            <div className="border border-dashed rounded-lg p-4 bg-muted/30">
              <p className="text-sm font-medium text-foreground">Appointment cancelled</p>
              <p className="text-xs text-muted-foreground mt-1">
                You can book a new appointment from the notarization detail page.
              </p>
              <Button asChild size="sm" className="mt-3">
                <Link href={`${basePath}/notarizations/${notarization.id}/appointments/new`}>
                  <CalendarDays className="w-3.5 h-3.5 mr-1.5" />
                  Book New Appointment
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}
